
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import type { AppView } from '../types';

interface HeaderProps {
  onOpenRecipe: () => void;
  onOpenGuidelines: () => void;
  onLogout: () => void;
  onSetView?: (view: AppView) => void;
  onOpenSettings?: () => void;
  onToggleNav?: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenRecipe, onOpenGuidelines, onLogout, onSetView, onOpenSettings, onToggleNav }) => {
  const { user } = useAuth();
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const DropdownItem: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
    <button
      onClick={() => {
        onClick();
        setDropdownOpen(false);
      }}
      className="block w-full text-left px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:bg-white/10 hover:text-[var(--color-text-primary)] transition-colors"
    >
      {children}
    </button>
  );

  return (
    <header className="bg-[var(--color-bg-glass)] border-b border-[var(--color-border-glass)] flex-shrink-0 backdrop-blur-md sticky top-0 z-30">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            {onToggleNav && (
                <button onClick={onToggleNav} className="mr-2 p-2 rounded-md text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-white/10 lg:hidden">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                </button>
            )}
            <div className="flex-shrink-0">
               <div className="text-2xl font-bold text-[var(--color-text-primary)] tracking-tighter cursor-pointer" onClick={() => onSetView?.('main')}>
                  출.정 <span className="text-lg font-medium text-[var(--color-text-secondary)] hidden sm:inline">| 출제자 정복하기</span>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 flex items-center gap-4">
            {user && (
              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(prev => !prev)}
                  className="flex items-center gap-2 p-2 rounded-full hover:bg-white/10 transition-colors"
                >
                  <div className="w-8 h-8 bg-[var(--color-accent)] rounded-full flex items-center justify-center text-white font-bold text-sm">
                    {user.email?.[0].toUpperCase()}
                  </div>
                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-[var(--color-text-secondary)] transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>

                {isDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-56 origin-top-right bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-md shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1 z-50">
                    <div className="px-4 py-2 border-b border-[var(--color-border)]">
                      <p className="text-sm text-[var(--color-text-primary)] font-medium truncate">{user.email}</p>
                    </div>
                    {onSetView && <DropdownItem onClick={() => onSetView('my-page')}>마이페이지</DropdownItem>}
                    <DropdownItem onClick={onOpenRecipe}>비밀레시피</DropdownItem>
                    <DropdownItem onClick={onOpenGuidelines}>해설강령</DropdownItem>
                    {onOpenSettings && <DropdownItem onClick={onOpenSettings}>설정</DropdownItem>}
                    <div className="border-t border-[var(--color-border)] my-1"></div>
                    <DropdownItem onClick={onLogout}>로그아웃</DropdownItem>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
